'use client';

import React from 'react';
import { Select, SelectTrigger, SelectValue, SelectContent } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { FilterSortType } from '@/components/sections/builder/elements/body/collection-product-grid/content';
import { BaseProductCategory } from '@/types/http/product/common';
import CardCategory from '@/components/sections/builder/elements/body/collection-product-grid/components/card-category';
import { AVAILABILITY_OPTIONS } from '@/constant/data';
import { ProductFilterType } from '@/components/sections/builder/filter/vertical-filter';
import { Star } from 'lucide-react';
import { ThemeType } from '@/stores/builder';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';

interface HorizontalFilterProps {
  data: ProductFilterType;
  categories?: BaseProductCategory[];
  filter: FilterSortType;
  price: { min: string; max: string };
  setCategory?: React.Dispatch<React.SetStateAction<string>>;
  handleResetFilter: () => void;
  handleCheckedChange: (type: 'inStock' | 'outStock') => void;
  handlePriceChange: (type: 'min' | 'max', value: string) => void;
  handleSelectCategory?: (id: string, name: string) => void;
  isLoadingCategory?: boolean;
  isFilterCategory?: boolean;
  handleRatingFilter: (rating: number) => void;
  isActiveReviewProduct?: boolean;
  isPreview?: boolean;
  theme: ThemeType;
}

export default function HorizontalFilter({
  data,
  categories,
  filter,
  price,
  setCategory,
  handleResetFilter,
  handleCheckedChange,
  handlePriceChange,
  handleSelectCategory,
  isLoadingCategory,
  isFilterCategory = true,
  handleRatingFilter,
  isActiveReviewProduct,
  isPreview,
  theme,
}: HorizontalFilterProps) {
  const starColor = theme?.productRating?.starColor || DEFAULT_THEME_COLOR_SCHEME.text;
  return (
    <div className='hidden items-center space-x-4 sm:flex'>
      <span className='font-medium'>Filter:</span>
      {isFilterCategory && (data?.showFilterCategory ?? true) && (
        <Select>
          <SelectTrigger className='w-32'>
            <SelectValue placeholder='Category' />
          </SelectTrigger>
          <SelectContent>
            <div className='w-[300px]'>
              <CardCategory
                colorScheme={data.colorScheme}
                categories={categories as BaseProductCategory[]}
                filter={filter}
                isLoading={isLoadingCategory as boolean}
                setCategory={setCategory as React.Dispatch<React.SetStateAction<string>>}
                onSelectCategory={handleSelectCategory as (id: string, name: string) => void}
                isHideTitle
              />
            </div>
          </SelectContent>
        </Select>
      )}
      {(data?.showFilterAvailability ?? true) && (
        <Select>
          <SelectTrigger className='w-32'>
            <SelectValue placeholder='Availability' />
          </SelectTrigger>
          <SelectContent>
            <div className='w-[250px]'>
              <div className='flex items-center justify-between p-3'>
                <Label>
                  {filter.availability?.includes('1') && filter.availability?.includes('0')
                    ? '2'
                    : filter.availability?.includes('1') || filter.availability?.includes('0')
                    ? '1'
                    : '0'}{' '}
                  selected
                </Label>
                <Button
                  variant='link'
                  className='underline'
                  onClick={handleResetFilter}
                  style={{ color: data.colorScheme?.btnSolidBackgroud }}
                >
                  Reset
                </Button>
              </div>
              <Separator />
              <div className='flex flex-col space-y-3 p-3'>
                {AVAILABILITY_OPTIONS.map(({ id, label, value }) => {
                  const isChecked = filter.availability?.includes(value);
                  const checkedStyle = isChecked
                    ? {
                        color: 'white',
                        borderColor: data.colorScheme?.btnSolidBackgroud,
                        backgroundColor: data.colorScheme?.btnSolidBackgroud,
                      }
                    : { borderColor: data.colorScheme?.btnSolidBackgroud };

                  return (
                    <Label key={id} className='flex items-center gap-2 font-normal'>
                      <Checkbox
                        id={id}
                        checked={isChecked}
                        onCheckedChange={() =>
                          handleCheckedChange(value === '1' ? 'inStock' : 'outStock')
                        }
                        style={checkedStyle}
                        isCustom
                      />{' '}
                      {label}
                    </Label>
                  );
                })}
              </div>
            </div>
          </SelectContent>
        </Select>
      )}
      {(data?.showFilterPrice ?? true) && (
        <Select>
          <SelectTrigger className='w-32'>
            <SelectValue placeholder='Price' />
          </SelectTrigger>
          <SelectContent>
            <div className='w-[348px]'>
              <div className='flex items-center justify-between p-3'>
                <p>The highest price is 15.000.000</p>
                <Button
                  variant='link'
                  className='text-black underline'
                  onClick={handleResetFilter}
                  style={{ color: data.colorScheme?.btnSolidBackgroud }}
                >
                  Reset
                </Button>
              </div>
              <Separator />
              <div className='flex gap-3 p-3'>
                <div className='flex items-center gap-2'>
                  <Label className='flex items-center gap-2 font-normal'>Rp</Label>
                  <Input
                    placeholder='From'
                    className=''
                    value={price.min}
                    onChange={(e) => handlePriceChange('min', e.target.value)}
                  />
                </div>
                <Input
                  placeholder='To'
                  className=''
                  value={price.max}
                  onChange={(e) => handlePriceChange('max', e.target.value)}
                />
              </div>
            </div>
          </SelectContent>
        </Select>
      )}

      {(isPreview || isActiveReviewProduct) && (
        <div className='flex flex-col'>
          <div className='flex w-full items-center gap-2'>
            {[5, 4, 3, 2, 1].map((rating) => {
              const isSelected = filter.rating === rating;
              return (
                <Button
                  key={rating}
                  variant={isSelected ? 'default' : 'outline'}
                  className='gap-x-1 px-[11px]'
                  onClick={() => handleRatingFilter(rating)}
                  style={
                    isSelected
                      ? {
                          backgroundColor: data.colorScheme?.btnSolidBackgroud,
                          color: data.colorScheme?.btnSolidlabel,
                        }
                      : {}
                  }
                >
                  <Star
                    className='h-4 w-4 '
                    style={{
                      color: starColor,
                      fill: starColor,
                    }}
                  />
                  <span>{rating}</span>
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
